<template>
  <v-main class="team-edit-main">
    <v-container class="pa-6">
      <v-row class="mb-4">
        <v-col cols="12">
          <div class="d-flex gap-2">
            <v-btn
                @click="save()"
                color="primary"
                variant="elevated"
                class="modern-btn"
                prepend-icon="mdi-content-save"
            >
              Save
            </v-btn>
            <v-btn
                @click="$router.go(-1)"
                variant="outlined"
                class="modern-btn"
                prepend-icon="mdi-arrow-left"
            >
              Cancel
            </v-btn>
          </div>
        </v-col>
      </v-row>

      <v-row class="mb-4">
        <v-col cols="12" md="6">
          <v-card class="modern-card" elevation="2">
            <v-card-title class="card-header">
              <v-icon class="me-3" color="primary">mdi-account-group</v-icon>
              Team Information
            </v-card-title>
            <v-card-text class="pa-4">
              <v-text-field
                  v-model="teamConfig.name"
                  label="Team Name"
                  variant="outlined"
                  density="comfortable"
              ></v-text-field>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mb-4">
        <v-col cols="12" md="6">
          <v-card class="modern-card" elevation="2">
            <v-card-title class="card-header">
              <v-icon class="me-3" color="success">mdi-account-multiple</v-icon>
              Groups
            </v-card-title>
            <v-card-text class="pa-4">
              <v-text-field
                  v-model="newGroupName"
                  label="New group name"
                  append-icon="mdi-plus"
                  variant="outlined"
                  density="comfortable"
                  class="mb-4"
                  @click:append="addGroup"
                  @keyup.enter="addGroup"
              ></v-text-field>
              <v-list v-if="teamConfig.groups && teamConfig.groups.length > 0" class="group-list">
                <v-list-item
                    v-for="(item, index) in teamConfig.groups"
                    :key="item"
                    class="list-item-modern"
                >
                  <v-list-item-title>{{ item }}</v-list-item-title>
                  <template v-slot:append>
                    <v-btn
                        size="small"
                        color="error"
                        variant="outlined"
                        icon="mdi-delete"
                        @click="removeGroup(index)"
                    ></v-btn>
                  </template>
                </v-list-item>
              </v-list>
              <div v-else class="text-center py-4 text-medium-emphasis">
                <v-icon class="mb-2">mdi-information-outline</v-icon>
                <p class="text-body-2">No groups added yet</p>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mb-4">
        <v-col cols="12" md="6">
          <v-card class="modern-card" elevation="2">
            <v-card-title class="card-header">
              <v-icon class="me-3" color="info">mdi-account-tie</v-icon>
              Coaches
            </v-card-title>
            <v-card-text class="pa-4">
              <v-text-field
                  v-model="newCoach"
                  label="New coach email"
                  append-icon="mdi-plus"
                  type="email"
                  variant="outlined"
                  density="comfortable"
                  class="mb-4"
                  @click:append="addCoach"
                  @keyup.enter="addCoach"
              ></v-text-field>
              <v-list v-if="teamConfig.coaches && teamConfig.coaches.length > 0" class="coach-list">
                <v-list-item
                    v-for="(item, index) in teamConfig.coaches"
                    :key="item"
                    class="list-item-modern"
                >
                  <v-list-item-title>{{ item.email }}</v-list-item-title>
                  <template v-slot:append>
                    <v-btn
                        size="small"
                        color="error"
                        variant="outlined"
                        icon="mdi-delete"
                        @click="removeCoach(index)"
                    ></v-btn>
                  </template>
                </v-list-item>
              </v-list>
              <div v-else class="text-center py-4 text-medium-emphasis">
                <v-icon class="mb-2">mdi-information-outline</v-icon>
                <p class="text-body-2">No coaches added yet, add the email address of other coaches you want to give
                  this team access to</p>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6">
          <v-card class="modern-card" elevation="2">
            <v-card-title class="card-header">
              <v-icon class="me-3" color="indigo">mdi-cog</v-icon>
              Configuration
            </v-card-title>
            <v-card-text class="pa-4">
              <v-text-field
                  type="number"
                  label="Water Break Length (minutes)"
                  v-model="teamConfig.waterBreakLength"
                  variant="outlined"
                  density="comfortable"
              ></v-text-field>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row v-if="teamConfig.id" class="mt-4">
        <v-col cols="12" md="6">
          <v-card class="modern-card" elevation="2">
            <v-card-title class="card-header d-flex align-center">
              <v-icon class="me-3" color="primary">mdi-clipboard-list-outline</v-icon>
              Practice Templates
              <v-spacer></v-spacer>
              <v-btn
                  color="primary"
                  variant="flat"
                  size="small"
                  class="text-none"
                  prepend-icon="mdi-plus"
                  @click="newTemplate"
              >
                New Template
              </v-btn>
            </v-card-title>
            <v-card-text class="pa-4">
              <v-list v-if="templates.length" class="group-list">
                <v-list-item
                    v-for="t in templates"
                    :key="t.id"
                    :to="{ name: 'templateedit', params: { teamid: teamConfig.id, templateid: t.id } }"
                    class="list-item-modern"
                >
                  <v-list-item-title class="d-flex align-center ga-2">
                    {{ t.name }}
                    <v-chip v-if="t.isDefault" size="x-small" color="primary" variant="tonal">Default</v-chip>
                  </v-list-item-title>
                  <v-list-item-subtitle>{{ templateSummary(t) }}</v-list-item-subtitle>
                  <template v-slot:append>
                    <v-icon>mdi-chevron-right</v-icon>
                  </template>
                </v-list-item>
              </v-list>
              <div v-else class="text-center py-4 text-medium-emphasis">
                <v-icon class="mb-2">mdi-information-outline</v-icon>
                <p class="text-body-2">
                  No templates yet. A template lays out a standard practice: the time slots and the kind of drill for
                  each one. New practice plans can start from it.
                </p>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

    </v-container>
  </v-main>

</template>

<script>

import api from '../api'

function getTeam(id, resultHandler) {

  if (id === "new") {
    resultHandler({
      name: "",
      waterBreakLength: 5,
    }, null);
  } else {
    api.teams.get(id, resultHandler)
  }

}

import {currentUserStore} from "@/stores/currentUserStore";

export default {
  name: 'TeamEdit',
  data() {
    return {
      teamConfig: {},
      templates: [],
      newGroupName: null,
      newCoach: null,
    }
  },
  beforeRouteEnter(to, _from, next) {
    getTeam(to.params.id, (team, err) => {
      next(vm => vm.setData(err, team))
    })
  },
  beforeRouteUpdate(to, _from) {
    getTeam(to.params.id, (team, err) => this.setData(err, team))
  },
  methods: {
    save() {
      console.log("about to save team")
      api.teams.save(this.teamConfig, () => {
        currentUserStore().refresh(() => {
          this.$router.push("/");
        });

      }, (e) => {
        this.$toast.open({message: 'Unable to save team: ' + e.response.data.message, type: 'error', duration: 5000});
      })

    },
    removeGroup(index) {
      this.teamConfig.groups.splice(index, 1);
    },
    removeCoach(index) {
      this.teamConfig.coaches.splice(index, 1);
    },
    addGroup() {
      if (!this.teamConfig.groups) {
        this.teamConfig.groups = [];
      }

      if (!this.newGroupName) {
        return;
      }

      this.teamConfig.groups.push(this.newGroupName);
      this.newGroupName = null;
    },
    addCoach() {
      if (!this.teamConfig.coaches) {
        this.teamConfig.coaches = [];
      }

      if (!this.newCoach) {
        return;
      }

      this.teamConfig.coaches.push({"email": this.newCoach});
      this.newCoach = null;
    },
    setData(error, post) {
      if (error) {
        this.error = error
      } else {
        this.teamConfig = post
        this.loadTemplates();
      }
    },
    loadTemplates() {
      this.templates = [];
      if (!this.teamConfig.id) {
        return;
      }
      api.templates.list(this.teamConfig.id, (templates) => {
        this.templates = (templates || []).sort((a, b) =>
            (b.isDefault - a.isDefault) || (a.name || '').localeCompare(b.name || ''));
      });
    },
    templateSummary(template) {
      const slots = template.timeSlots || [];
      const minutes = slots.reduce((sum, s) => sum + (s.duration || 0), 0);
      return `${slots.length} ${slots.length === 1 ? 'slot' : 'slots'} · ${minutes} min`;
    },
    newTemplate() {
      api.templates.new(this.teamConfig.id, (template) => {
        this.$router.push({name: 'templateedit', params: {teamid: this.teamConfig.id, templateid: template.id}})
      }, (e) => {
        this.$toast.open({
          message: 'Unable to create new template: ' + e.response.data.message,
          type: 'error',
          duration: 5000
        });
      })

    },
  }
}
</script>
<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
/* Main container styling */
.team-edit-main {
  background: linear-gradient(135deg, var(--surface-sunken) 0%, var(--surface-sunken-end) 100%);
  min-height: 100vh;
}

/* Modern card styling */
.modern-card {
  border-radius: var(--radius-lg) !important;
  transition: all 0.3s ease;
  border: 1px solid var(--border-subtle);
}

.modern-card:hover {
  transform: none;
  box-shadow: var(--shadow-lg) !important;
}

.card-header {
  background: linear-gradient(135deg, var(--surface-sunken) 0%, var(--surface-sunken-end) 100%);
  border-bottom: 1px solid var(--border-subtle);
  font-weight: 600;
  padding: var(--space-5) var(--space-6);
}

/* Button styling */
.modern-btn {
  border-radius: var(--radius-none) !important;
  text-transform: none !important;
  font-weight: 500 !important;
  letter-spacing: 0.5px !important;
}

/* All buttons in this component */
.v-btn {
  border-radius: var(--radius-none) !important;
}

/* List styling */
.group-list,
.coach-list {
  background: transparent;
}

.list-item-modern {
  border-radius: var(--radius-md);
  margin-bottom: var(--space-2);
  transition: all 0.2s ease;
  border: 1px solid var(--border-subtle);
}

.list-item-modern:hover {
  background-color: rgba(0, 0, 0, 0.04);
  transform: none;
}

/* Text field styling */
.v-text-field .v-field {
  border-radius: var(--radius-md) !important;
}

/* Progress bar styling */
.v-progress-linear {
  border-radius: var(--radius-xs) !important;
}

/* Chip styling */
.v-chip {
  border-radius: var(--radius-sm) !important;
  font-weight: 500 !important;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .modern-card {
    margin-bottom: var(--space-4);
  }

  .card-header {
    padding: var(--space-4) var(--space-5);
  }
}

/* Animation for list items */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.list-item-modern {
  animation: slideIn 0.3s ease-out;
}

/* Custom scrollbar for lists */
.v-list {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}

.v-list::-webkit-scrollbar {
  width: 6px;
}

.v-list::-webkit-scrollbar-track {
  background: transparent;
}

.v-list::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: var(--radius-xs);
}

.v-list::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.3);
}
</style>